// ── financeUtils.js — shared constants, utilities, and UI components ──────────
// Used by Finance.js, SupplierSpendTab.js, PLTab.js, CashTab.js

import { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';

// ── Colour system ─────────────────────────────────────────────────────────────
export const C = {
  pageBg:      '#F7F6EF',
  white:       '#FFFFFF',
  rowAlt:      '#F7F6EF',
  sectionBg:   '#FAFAF7',
  ink:         '#2F3A42',
  mid:         '#7A8A96',
  faint:       '#A8B5BD',
  border:      '#D8DEE3',
  borderMid:   '#C4CDD3',
  navy:        '#445366',
  navyLight:   '#EAF4F8',
  charcoal:    '#2F3A42',
  green:       '#7EA594',
  greenBg:     '#EAF2EF',
  red:         '#C0424F',
  redBg:       '#FFF0F0',
  amber:       '#E07B00',
  amberBg:     '#FFF3E0',
  yellow:      '#F7EC71',
  skyText:     '#3A8FA3',
  skyBg:       '#EAF4F8',
  skyBlueLight:'#EAF4F8',
  cream:       '#F7F6EF',
  greenLight:  '#EAF2EF',
  redLight:    '#FFF0F0',
};

export const SUPABASE_FUNCTIONS_URL = 'https://unvzfprqkmxrifwodlec.supabase.co/functions/v1';

// ── Account groupings ─────────────────────────────────────────────────────────
export const GROUPS = {
  LM_INCOME:     ['Food Sales', 'Lekker Mate Sauces', 'Management Fees', 'Kitchen Prep Service', 'Returned Oil Income'],
  SASSYS_INCOME: ["Sassy's Revenue"],
  LM_COGS:       ['COGS', 'LM Sauces - COS'],
  SASSYS_COGS:   ["Sassy's Food Cost"],
  SASSYS_OPEX:   ["Sassy's Rent & Utilities", "Sassy's Other Expenses", 'Oedipus Revenue Share'],
  SASSYS_SOLO:   ["Sassy's Marketing"],
  LM_PAYROLL:    ['Lekker Mate Payroll', 'Wages and Salaries', 'Pension Expense \u2013 Employer'],
  SASSYS_PAYROLL:["Sassy's Payroll"],
};

export function getGroup(name) {
  for (const [key, names] of Object.entries(GROUPS)) {
    if (names.includes(name)) return key;
  }
  return 'LM_OPEX';
}

export function belongsToEntity(name, entity) {
  if (entity === 'both') return true;
  const sassys = [...GROUPS.SASSYS_INCOME, ...GROUPS.SASSYS_COGS, ...GROUPS.SASSYS_OPEX, ...GROUPS.SASSYS_SOLO, ...GROUPS.SASSYS_PAYROLL];
  const isSassy = sassys.includes(name);
  if (entity === 'sassys')     return isSassy;
  if (entity === 'lekkermate') return !isSassy;
  return true;
}

export const XERO_INJECTED_ROWS = new Set(['Gross Profit', 'Net Profit', 'Total Income',
  'Total Cost of Sales', 'Total Operating Expenses', 'Total Other Income']);

// Returns { map: {name->value}, codes: {name->accountUUID} }
export function parsePL(reportData) {
  const map = {}, codes = {};
  const report = reportData?.Reports?.[0];
  if (!report) return { map, codes };
  (report.Rows || []).forEach(section => {
    (section.Rows || []).forEach(row => {
      if (row.RowType !== 'Row') return;
      const name = row.Cells?.[0]?.Value || '';
      const val  = parseFloat(row.Cells?.[1]?.Value || '0') || 0;
      if (!name) return;
      if (XERO_INJECTED_ROWS.has(name)) return;
      if (val === 0) return;
      map[name] = (map[name] || 0) + val;
      const attrs = row.Cells?.[0]?.Attributes || [];
      const acctAttr = attrs.find(a => a.Id === 'account');
      if (acctAttr?.Value && !codes[name]) codes[name] = acctAttr.Value;
    });
  });
  return { map, codes };
}

export function getOpexNames(maps, entity) {
  const names = new Set();
  Object.values(maps).forEach(m => {
    Object.keys(m).forEach(n => {
      if (getGroup(n) === 'LM_OPEX' && belongsToEntity(n, entity)) names.add(n);
    });
  });
  return [...names].sort();
}

// ── Utilities ─────────────────────────────────────────────────────────────────
export function fmt(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}
export function fmtFull(n) {
  if (n == null || isNaN(n)) return '—';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n);
}
export function parseXeroDate(d) {
  if (!d) return null;
  const ms = parseInt(d.replace('/Date(', '').replace('+0000)/', '').replace(')/', ''));
  return isNaN(ms) ? null : new Date(ms);
}
export function fmtDate(d) {
  const dt = parseXeroDate(d);
  return dt ? dt.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}
export function mKey(d) {
  const dt = parseXeroDate(d);
  return dt ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}` : null;
}
export function getLast12Months() {
  const now = new Date(), months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      label: d.toLocaleString('en-NL', { month: 'short', year: '2-digit' }),
      from:  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
      to:    new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0],
      key:   `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    });
  }
  return months;
}
export function isSupplierHidden(c, role) {
  if (role === 'admin') return false;
  return c.admin_only === true;
}
export function useIsMobile() {
  const [mob, setMob] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

// ── Shared UI components ──────────────────────────────────────────────────────
export function Spinner({ label = 'Loading…' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: C.mid }}>
      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: '0.85rem' }}>{label}</span>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export function ErrorBanner({ message }) {
  return (
    <div style={{ background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 10, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem', color: C.red, fontSize: '0.85rem', margin: '1rem 0' }}>
      <AlertCircle size={16} /><span>{message}</span>
    </div>
  );
}

export function StatRow({ cards }) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      display: 'flex', gap: '0.65rem',
      overflowX: isMobile ? 'auto' : 'visible',
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      marginBottom: '1.25rem',
      paddingBottom: isMobile ? '4px' : 0,
      WebkitOverflowScrolling: 'touch',
    }}>
      {cards.map((card, i) => (
        <div key={i} style={{
          background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
          padding: '0.9rem 1.1rem', flexShrink: 0,
          minWidth: isMobile ? 130 : 140,
          flex: isMobile ? '0 0 auto' : '1 1 130px',
          borderTop: `3px solid ${card.accent || C.navy}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontSize: '0.7rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{card.label}</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: C.ink, letterSpacing: '-0.02em' }}>{card.value}</div>
          {card.sub && <div style={{ fontSize: '0.72rem', color: C.mid, marginTop: 2 }}>{card.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export function PillToggle({ options, value, onChange, accent }) {
  return (
    <div style={{ display: 'inline-flex', background: C.rowAlt, borderRadius: 10, padding: 3, border: `1px solid ${C.border}`, gap: 2 }}>
      {options.map(o => (
        <button key={o.key} onClick={() => onChange(o.key)} style={{
          padding: '0.35rem 0.8rem', borderRadius: 7, fontSize: '0.8rem', fontWeight: 700,
          cursor: 'pointer', border: 'none', transition: 'all 0.12s',
          background: value === o.key ? (accent || C.navy) : 'transparent',
          color: value === o.key ? C.white : C.mid,
          whiteSpace: 'nowrap',
        }}>{o.label}</button>
      ))}
    </div>
  );
}

export function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.ink, color: C.white, borderRadius: 8, padding: '0.5rem 0.85rem', fontSize: '0.8rem', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => <div key={i}>{fmt(p.value)}</div>)}
    </div>
  );
}

export function StatusPill({ status, dueDate, invoiceType }) {
  const now = new Date(), due = parseXeroDate(dueDate);
  if (invoiceType === 'SPEND') return <span style={{ background: C.greenBg, color: C.green, borderRadius: 6, padding: '0.2rem 0.55rem', fontSize: '0.73rem', fontWeight: 700 }}>Paid</span>;
  const overdue = status === 'AUTHORISED' && due && due < now;
  const isIncome = invoiceType === 'ACCREC';
  const map = isIncome
    ? {
        PAID:       { label: 'Received',   bg: '#e8f5ef',  color: '#3a7d5e' },
        AUTHORISED: { label: 'Receivable', bg: C.skyBg,    color: C.skyText },
        DRAFT:      { label: 'Draft',      bg: C.rowAlt,   color: C.mid    },
        VOIDED:     { label: 'Voided',     bg: C.rowAlt,   color: C.mid    },
      }
    : {
        PAID:       { label: 'Paid',     bg: C.greenBg,  color: C.green  },
        AUTHORISED: { label: overdue ? 'Overdue' : 'Open', bg: overdue ? C.redBg : C.amberBg, color: overdue ? C.red : C.amber },
        VOIDED:     { label: 'Voided',   bg: C.rowAlt,   color: C.mid    },
        DRAFT:      { label: 'Draft',    bg: C.skyBg,    color: C.skyText },
      };
  const s = map[status] || { label: status, bg: C.rowAlt, color: C.mid };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: '0.2rem 0.55rem', fontSize: '0.73rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{s.label}</span>;
}